import { effect, Injectable, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { WebSocketService } from './web-socket.service';
import { TopicType } from '../enums/topic-type';
import { ReportStore } from '@stores/report.store';
import {
  Report,
  ReportFilters,
  SortOrder,
  EmailSettings,
  ReportExportRequest,
} from '@interfaces/reports/reports';
import { ReportingApiService } from './reporting-api.service';
import {
  ReportingFilterOptionsResponse,
  ReportingLocationOption,
} from '@interfaces/reports/reporting-filter-options.response';
import {
  ReportingGenerateRequest,
  ReportingGenerateResponse,
  ReportingPagination,
} from '@interfaces/reports/reporting-generate.response';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ToastService } from './toast.service';
import { User } from '@interfaces/reports/reports.read.resp';
import {
  ReportingTemplateDetail,
  ReportingTemplateSummary,
  ReportingTemplateCreateRequest,
  ReportingTemplateUpdateRequest,
} from '@interfaces/reports/reporting-templates.response';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private reportStore = inject(ReportStore);
  private readonly timezone = 'America/Toronto';

  constructor(
    private authService: AuthService,
    private webSocketService: WebSocketService,
    private reportingApiService: ReportingApiService,
    private toastService: ToastService,
  ) {
    effect(() => {});
  }

  loadReportTemplates() {
    const username = this.authService.user().username;
    if (!username) {
      return throwError(
        () => new Error('Unable to load templates: username is missing'),
      );
    }

    return this.reportingApiService.getTemplates(username).pipe(
      tap((response) => this.applyTemplates(response)),
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }

  loadTemplateDetails(templateId: string) {
    return this.reportingApiService.getTemplateById(templateId).pipe(
      tap((detail) => this.upsertTemplateDetail(detail, true)),
      map((detail) => this.mapTemplateDetailToReport(detail)),
      tap((report) => this.reportStore.updateReport(report)),
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }

  loadFilterOptions() {
    return this.reportingApiService.getFilterOptions().pipe(
      tap((response) => this.applyFilterOptions(response)),
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }

  generateReports(options?: {
    filters?: ReportFilters;
    page?: number;
    pageSize?: number;
    sortOrder?: SortOrder;
    showToast?: boolean;
  }) {
    const requestPayload = this.buildGenerateRequest(options);
    const showToast = options?.showToast ?? true;
    return this.reportingApiService.generateReports(requestPayload).pipe(
      tap((response) =>
        this.processGenerateResponse(response, requestPayload, showToast),
      ),
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }

  exportReport(
    format: 'pdf' | 'csv',
    title: string,
  ): Observable<HttpResponse<Blob>> {
    const payload: ReportExportRequest = {
      title,
      filters: {
        ...this.reportStore.filter(),
        timezone: this.timezone,
      },
    };

    const request$ =
      format === 'pdf'
        ? this.reportingApiService.exportReportPdf(payload)
        : this.reportingApiService.exportReportCsv(payload);

    return request$.pipe(
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }

  private applyFilterOptions(response: ReportingFilterOptionsResponse) {
    const current = this.reportStore.reportFilters();
    this.reportStore.setReportFilters({
      ...current,
      codeTypes: response.codeTypes ?? [],
      departments: response.departments ?? [],
      locations: this.normalizeLocations(response.locations),
      nurseCallTypes: response.nurseCallTypes ?? [],
      alarmTypes: response.alarmTypes ?? [],
      users: this.mapUsernamesToUsers(response.users),
    });
  }

  private applyTemplates(templates: ReportingTemplateSummary[]) {
    const current = this.reportStore.reportFilters();
    const reports = templates.map((template) =>
      this.mapTemplateSummaryToReport(template),
    );
    this.reportStore.setReportFilters({
      ...current,
      reports,
    });
  }

  private normalizeLocations(
    locations?: ReportingLocationOption[],
  ): ReportingLocationOption[] {
    if (!Array.isArray(locations)) {
      return [];
    }

    return locations.map((location) => ({
      name: location.name,
      department: location.department,
    }));
  }

  private mapUsernamesToUsers(usernames?: string[]): User[] {
    return (usernames ?? []).map((username, index) => ({
      id: `${username}-${index}`,
      username,
      name: username,
    }));
  }

  sendReportsRequestRead() {
    const userId = this.authService.user().id;
    const userName = this.authService.user().username;

    const message: object = {
      requester: userId!.replace('@', ''),
      operation: 'read',
      parameters: {
        ownerUsername: userName,
      },
    };
    this.webSocketService.send({
      topic: TopicType.MANAGE_REPORTS_REQUEST,
      message,
    });
  }

  setCurrentFilterId(currentFilterId: string) {
    this.reportStore.setCurrentFilterId(currentFilterId);
  }

  setReadReports(data: any) {
    this.reportStore.setReportFilters(data.message.data);
  }

  addReadReports(data: any) {
    this.reportStore.addReportFilter(data);
  }

  setReports(data: any) {
    const safeData = data || {};
    this.reportStore.setReports([
      ...(Array.isArray(safeData.codes) ? safeData.codes : []),
      ...(Array.isArray(safeData.alarms) ? safeData.alarms : []),
      ...(Array.isArray(safeData.nurseCalls) ? safeData.nurseCalls : []),
    ]);
  }

  updateReport(data: any) {
    this.reportStore.updateReport(data);
  }

  deleteFilter() {
    const reportId = this.reportStore.currentFilterId();
    if (reportId) {
      return this.deleteTemplate(String(reportId));
    }
    return throwError(() => new Error('No report selected'));
  }

  deleteFilterFromStore(id: string | number) {
    this.reportStore.deleteReportFilter(String(id));
  }

  saveFilter(title: string, emailSettings?: EmailSettings) {
    const filters = this.reportStore.filter();

    const userId = this.authService.user().id;

    const userName = this.authService.user().username;

    const payload: ReportingTemplateCreateRequest = {
      ownerUsername: userName,
      title,
      filters: this.withTimezone(filters),
      emailSettings,
    };

    return this.createTemplate(payload);
  }

  updateFilter(title: string, emailSettings?: EmailSettings) {
    const filters = this.reportStore.filter();

    const userId = this.authService.user().id;
    const filterId = this.reportStore.currentFilterId();

    const userName = this.authService.user().username;
    const payload: ReportingTemplateUpdateRequest = {
      title,
      filters: this.withTimezone(filters),
      emailSettings,
    };

    return this.updateTemplate(String(filterId), payload);
  }

  setFilters(filter: ReportFilters) {
    this.reportStore.setFilters(filter);
  }

  getCurrentFiltersSnapshot(): ReportFilters {
    return this.reportStore.filter();
  }

  private buildGenerateRequest(options?: {
    filters?: ReportFilters;
    page?: number;
    pageSize?: number;
    sortOrder?: SortOrder;
  }): ReportingGenerateRequest {
    const pagination = this.reportStore.reportPagination();
    const currentSortOrder = this.reportStore.reportSortOrder();
    const filters = options?.filters ?? this.reportStore.filter();
    return {
      filters: {
        ...filters,
        timezone: this.timezone,
      },
      page: options?.page ?? pagination.page ?? 1,
      pageSize: options?.pageSize ?? pagination.pageSize ?? 50,
      sortOrder: options?.sortOrder ?? currentSortOrder ?? 'DESC',
    };
  }

  private processGenerateResponse(
    response: ReportingGenerateResponse,
    requestPayload: ReportingGenerateRequest,
    showToast: boolean,
  ) {
    const normalizedReports = (response?.data ?? []).map((report) => ({
      ...report,
      responders: this.normalizeResponders(report.responders),
    }));

    this.reportStore.setReports(normalizedReports);

    const pagination = this.resolvePagination(
      response.pagination,
      requestPayload,
      normalizedReports.length,
    );
    this.reportStore.setReportPagination(pagination);
    this.reportStore.setReportSortOrder(requestPayload.sortOrder);

    if (showToast) {
      const messageKey =
        normalizedReports.length > 0
          ? 'front.reporting.resived-data'
          : 'front.reporting.no-data';
      this.toastService.showSuccessMessage(messageKey);
    }
  }

  private resolvePagination(
    pagination: ReportingPagination | undefined,
    requestPayload: ReportingGenerateRequest,
    totalItems: number,
  ): ReportingPagination {
    if (pagination) {
      return pagination;
    }

    return {
      page: requestPayload.page,
      pageSize: requestPayload.pageSize,
      totalItems,
      totalPages: requestPayload.pageSize
        ? Math.ceil(totalItems / requestPayload.pageSize)
        : 0,
    };
  }

  private normalizeResponders(
    responders: string[] | string | undefined,
  ): string[] {
    if (Array.isArray(responders)) {
      return responders;
    }

    if (typeof responders === 'string' && responders.trim().length > 0) {
      return responders
        .split(',')
        .map((name) => name.trim())
        .filter((name) => !!name);
    }

    return [];
  }

  private upsertTemplateDetail(
    detail: ReportingTemplateDetail,
    setAsCurrent = false,
  ) {
    const mapped = this.mapTemplateDetailToReport(detail);
    const currentFilters = this.reportStore.reportFilters();
    const filteredReports = currentFilters.reports.filter(
      (report) => report.id !== mapped.id,
    );
    this.reportStore.setReportFilters({
      ...currentFilters,
      reports: [...filteredReports, mapped],
    });

    if (setAsCurrent) {
      this.reportStore.setCurrentFilterId(mapped.id);
      this.reportStore.setFilters(mapped.filters);
    }
  }

  private mapTemplateSummaryToReport(
    template: ReportingTemplateSummary,
  ): Report {
    return {
      id: String(template.id),
      title: template.title,
      owner: {
        id: template.ownerUsername,
        name: template.ownerUsername,
        username: template.ownerUsername,
      },
      filters: this.createEmptyFilters(),
    };
  }

  private mapTemplateDetailToReport(detail: ReportingTemplateDetail): Report {
    return {
      id: String(detail.id),
      title: detail.title,
      owner: {
        id: detail.ownerUsername,
        name: detail.ownerUsername,
        username: detail.ownerUsername,
      },
      filters: detail.filters ?? this.createEmptyFilters(),
      emailSettings: detail.emailSettings,
    };
  }

  private createEmptyFilters(): ReportFilters {
    return {
      departments: 'NONE',
      locations: 'NONE',
      alarms: 'NONE',
      codes: 'NONE',
      nurseCalls: 'NONE',
      status: 'NONE',
      startDate: 'NONE',
      endDate: 'NONE',
      users: 'NONE',
    };
  }

  private withTimezone(filters: ReportFilters): ReportFilters {
    return {
      ...filters,
      timezone: this.timezone,
    };
  }

  createTemplate(payload: ReportingTemplateCreateRequest) {
    return this.reportingApiService.createTemplate(payload).pipe(
      tap((detail) => this.upsertTemplateDetail(detail, true)),
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }

  updateTemplate(templateId: string, payload: ReportingTemplateUpdateRequest) {
    return this.reportingApiService.updateTemplate(templateId, payload).pipe(
      tap((detail) => this.upsertTemplateDetail(detail, true)),
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }

  deleteTemplate(templateId: string) {
    return this.reportingApiService.deleteTemplate(templateId).pipe(
      tap(() => this.deleteFilterFromStore(templateId)),
      catchError((error) => {
        this.toastService.showInfoMessageWithErorrMessage(
          'front.reporting.error',
          error?.message ?? '',
        );
        return throwError(() => error);
      }),
    );
  }
}
